import React, { useState, useCallback, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Trash2, ChevronDown, ChevronUp, Info } from 'lucide-react';
import WardrobeLayoutPreview from './WardrobeLayoutPreview';
import { toast } from 'sonner';

const API_URL = '';

const MODULE_DEFAULTS = {
  wardrobe: { width: 8, height: 9.5, depth: 1.5, shutter_type: 'hinged' },
  tv_unit: { width: 6, height: 6, depth: 1.25, shutter_type: 'hinged' },
  study_desk: { width: 4, height: 2.5, depth: 2, shutter_type: 'hinged' },
  crockery_unit: { width: 4, height: 7, depth: 1.5, shutter_type: 'hinged' },
  shoe_rack: { width: 3, height: 4, depth: 1, shutter_type: 'hinged' },
  bar_unit: { width: 4, height: 5, depth: 1.25, shutter_type: 'hinged' },
  vanity_unit: { width: 4, height: 4, depth: 1.5, shutter_type: 'hinged' },
  pooja_unit: { width: 3, height: 6, depth: 1.5, shutter_type: 'hinged' },
  feature_wall: { width: 10, height: 9.5, depth: 0.5, shutter_type: 'hinged' },
  shelves: { width: 4, height: 6, depth: 1, shutter_type: 'hinged' },
};

const SHOWS_SHUTTER_TYPE = new Set(['wardrobe', 'crockery_unit', 'bar_unit', 'shoe_rack']);
const SHOWS_LAYOUT_PREVIEW = new Set(['wardrobe']);

const ALL_MODULE_TYPES = [
  { key: 'wardrobe', label: 'Wardrobe' },
  { key: 'tv_unit', label: 'TV Unit' },
  { key: 'study_desk', label: 'Study Desk' },
  { key: 'crockery_unit', label: 'Crockery Unit' },
  { key: 'shoe_rack', label: 'Shoe Rack' },
  { key: 'bar_unit', label: 'Bar Unit' },
  { key: 'vanity_unit', label: 'Vanity Unit' },
  { key: 'pooja_unit', label: 'Pooja Unit' },
  { key: 'feature_wall', label: 'Feature Wall / Accent Wall' },
  { key: 'shelves', label: 'Open Shelves' },
];

const DEFAULT_QUALITY_TIERS = [
  { key: 'basic', label: 'Basic', hint: 'Quality essentials at an affordable price' },
  { key: 'standard', label: 'Standard', hint: 'Reliable quality with branded soft-close hardware' },
  { key: 'premium', label: 'Premium', hint: 'High-quality finishes and premium branded hardware' },
  { key: 'luxury', label: 'Luxury', hint: 'Top-tier materials and finest craftsmanship' },
];

const TIER_COLORS = {
  basic: 'bg-stone-50 border-stone-300 text-stone-700',
  standard: 'bg-sky-50 border-sky-300 text-sky-700',
  premium: 'bg-amber-50 border-amber-300 text-amber-700',
  luxury: 'bg-violet-50 border-violet-300 text-violet-700',
};

const ModuleConfigurator = ({ modules, onModulesChange, panels, onPanelsChange, options, roomType, allowedModules }) => {
  const [expandedIdx, setExpandedIdx] = useState(0);
  const [loadingIdx, setLoadingIdx] = useState(null);
  const [results, setResults] = useState({});
  const debounceTimers = useRef({});

  const availableTypes = allowedModules && allowedModules.length > 0
    ? allowedModules
    : ALL_MODULE_TYPES;

  const moduleTypesForDropdown = options?.module_types?.filter(t => t.key !== 'kitchen') || ALL_MODULE_TYPES;
  const qualityTiers = options?.quality_tiers?.length > 0 ? options.quality_tiers : DEFAULT_QUALITY_TIERS;

  const createModule = (typeKey) => {
    const defaults = MODULE_DEFAULTS[typeKey] || MODULE_DEFAULTS.wardrobe;
    return {
      type: typeKey,
      module_name: '',
      width: defaults.width,
      height: defaults.height,
      depth: defaults.depth,
      quality_tier: 'standard',
      shutter_type: defaults.shutter_type,
    };
  };

  const addModuleOfType = (typeKey) => {
    const newMod = createModule(typeKey);
    const newModules = [...modules, newMod];
    onModulesChange(newModules);
    setExpandedIdx(modules.length);
    fetchModuleCost(modules.length, newMod);
  };

  const removeModule = (idx) => {
    const next = modules.filter((_, i) => i !== idx);
    onModulesChange(next);
    const newResults = { ...results };
    delete newResults[idx];
    setResults(newResults);
    if (expandedIdx >= next.length) setExpandedIdx(Math.max(0, next.length - 1));
  };

  const updateModule = (idx, field, value) => {
    const next = [...modules];
    if (field === 'type') {
      const defaults = MODULE_DEFAULTS[value] || MODULE_DEFAULTS.wardrobe;
      next[idx] = {
        ...next[idx],
        type: value,
        width: defaults.width,
        height: defaults.height,
        depth: defaults.depth,
        shutter_type: defaults.shutter_type,
      };
    } else {
      next[idx] = { ...next[idx], [field]: value };
    }
    onModulesChange(next);
    debounceFetchCost(idx, next[idx]);
  };

  const fetchModuleCost = useCallback(async (idx, moduleData) => {
    if (!moduleData.width || moduleData.width <= 0) return;
    setLoadingIdx(idx);
    try {
      const res = await fetch(`${API_URL}/api/interior-design/module-cost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ module: moduleData }),
      });
      if (!res.ok) throw new Error('Failed to calculate');
      const data = await res.json();
      setResults(prev => ({ ...prev, [idx]: data }));
    } catch {
      toast.error('Could not calculate module cost');
    } finally {
      setLoadingIdx(null);
    }
  }, []);

  const debounceFetchCost = useCallback((idx, moduleData) => {
    if (debounceTimers.current[idx]) clearTimeout(debounceTimers.current[idx]);
    debounceTimers.current[idx] = setTimeout(() => fetchModuleCost(idx, moduleData), 600);
  }, [fetchModuleCost]);

  return (
    <div data-testid="module-configurator" className="space-y-4">
      {modules.map((mod, idx) => {
        const isExpanded = expandedIdx === idx;
        const result = results[idx];
        const showShutter = SHOWS_SHUTTER_TYPE.has(mod.type);
        const showLayout = SHOWS_LAYOUT_PREVIEW.has(mod.type);
        const typeLabel = moduleTypesForDropdown.find(t => t.key === mod.type)?.label || mod.type?.replace(/_/g, ' ');
        const tierLabel = qualityTiers.find(t => t.key === (mod.quality_tier || 'standard'))?.label || 'Standard';

        return (
          <div key={idx} data-testid={`module-card-${idx}`}
               className="bg-white border border-stone-200 transition-all">
            <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-stone-50"
                 onClick={() => setExpandedIdx(isExpanded ? -1 : idx)}>
              <div className="flex items-center gap-3">
                {isExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
                <div>
                  <span className="font-medium text-stone-800">
                    {mod.module_name || typeLabel}
                  </span>
                  <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-stone-100 text-stone-500 font-mono">
                    {tierLabel}
                  </span>
                  <span className="ml-3 text-sm text-stone-500">
                    {mod.width}ft x {mod.height}ft x {mod.depth}ft
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {loadingIdx === idx && <Loader2 className="w-4 h-4 animate-spin text-stone-400" />}
                {result && (
                  <span data-testid={`module-cost-${idx}`} className="font-mono text-sm text-emerald-700 font-medium">
                    {'\u20B9'}{result.total_cost?.toLocaleString('en-IN')}
                  </span>
                )}
                <Button variant="ghost" size="sm"
                        data-testid={`remove-module-${idx}`}
                        onClick={(e) => { e.stopPropagation(); removeModule(idx); }}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>

            {isExpanded && (
              <div className="border-t border-stone-200 p-5 space-y-5">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-stone-500">Module Type</Label>
                    <Select value={mod.type} onValueChange={(v) => updateModule(idx, 'type', v)}>
                      <SelectTrigger className="rounded-none mt-1" data-testid={`module-type-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {moduleTypesForDropdown.map(t => (
                          <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-stone-500">Name (optional)</Label>
                    <Input className="rounded-none mt-1" data-testid={`module-name-${idx}`}
                           value={mod.module_name} placeholder="e.g. Master Wardrobe"
                           onChange={(e) => updateModule(idx, 'module_name', e.target.value)} />
                  </div>
                  {showShutter && (
                    <div>
                      <Label className="text-xs text-stone-500">Shutter Type</Label>
                      <Select value={mod.shutter_type} onValueChange={(v) => updateModule(idx, 'shutter_type', v)}>
                        <SelectTrigger className="rounded-none mt-1" data-testid={`module-shutter-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hinged">Hinged</SelectItem>
                          <SelectItem value="sliding">Sliding</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {/* Dimensions */}
                <div>
                  <Label className="text-xs text-stone-500 mb-2 block">Dimensions (feet)</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs">Width</Label>
                      <Input type="number" step="0.5" min="1" max="30" className="rounded-none mt-1 font-mono"
                             data-testid={`module-width-${idx}`}
                             value={mod.width}
                             onChange={(e) => updateModule(idx, 'width', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <Label className="text-xs">Height</Label>
                      <Input type="number" step="0.5" min="1" max="14" className="rounded-none mt-1 font-mono"
                             data-testid={`module-height-${idx}`}
                             value={mod.height}
                             onChange={(e) => updateModule(idx, 'height', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div>
                      <Label className="text-xs">Depth</Label>
                      <Input type="number" step="0.25" min="0.5" max="3" className="rounded-none mt-1 font-mono"
                             data-testid={`module-depth-${idx}`}
                             value={mod.depth}
                             onChange={(e) => updateModule(idx, 'depth', parseFloat(e.target.value) || 0)} />
                    </div>
                  </div>
                </div>

                {/* Quality Tier Selection */}
                <div>
                  <Label className="text-xs text-stone-500 mb-2 block">Material & Hardware Quality</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {qualityTiers.map(t => {
                      const isActive = (mod.quality_tier || 'standard') === t.key;
                      const colorClass = TIER_COLORS[t.key] || TIER_COLORS.standard;
                      return (
                        <button key={t.key} type="button" data-testid={`module-tier-${t.key}-${idx}`}
                          className={`p-3 border-2 text-left transition-all rounded ${isActive ? colorClass + ' ring-1 ring-offset-1' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                          onClick={() => updateModule(idx, 'quality_tier', t.key)}>
                          <span className={`font-semibold text-sm block ${isActive ? '' : 'text-stone-700'}`}>{t.label}</span>
                          <span className="text-[11px] text-stone-500 leading-snug block mt-0.5">{t.hint || t.description || ''}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Layout Preview - only for wardrobe */}
                {showLayout && result && result.layout && (
                  <div className="pt-3 border-t border-stone-100">
                    <Label className="text-xs text-stone-500 mb-2 block">Layout Preview</Label>
                    <WardrobeLayoutPreview
                      layout={result.layout}
                      totalWidth={mod.width}
                      height={mod.height}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Context-Aware Add Module Buttons */}
      <div className="flex gap-2 flex-wrap">
        {availableTypes.map(t => (
          <Button
            key={t.key}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => addModuleOfType(t.key)}
            data-testid={`add-module-${t.key}`}
            className="rounded-none text-xs border-dashed border-stone-300 text-stone-500 hover:border-stone-400 hover:text-stone-700"
          >
            <Plus className="w-3 h-3 mr-1" /> {t.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default ModuleConfigurator;
